import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit3, Trash2, Save, X, Eye, EyeOff, MapPin, Clock, FileDown, Building2, Plane, Loader2, FileText, Link2, Check } from 'lucide-react';
import { API, adminAuthHeader, resolveImg } from '../api';
import AdminSearch from './AdminSearch';
import ImageUploader from './ImageUploader';
import SEOSettingsBlock from './SEOSettingsBlock';

const empty = {
  pkg_code: '',
  title: '',
  category: 'india',
  destination: '',
  themes: '',
  duration: '',
  itinerary: '',
  price: 0,
  image: '',
  fbType: 'default',
  overview: '',
  highlights: '',
  inclusions: '',
  exclusions: '',
  hotels: [],
  flights: [],
  show_hotels: true,
  show_flights: true,
  additional_info: '',
  published: true,
  offer_enabled: false,
  offer_price: 0,
  offer_ribbon_text: 'Limited Offer',
  offer_ends_at: '',
  seo_title: '',
  seo_description: '',
  seo_og_image: '',
};

const emptyHotel = { name: '', city: '', nights: '', category: '', image: '', image2: '', notes: '' };
const emptyFlight = { airline: '', flight_no: '', from_city: '', to_city: '', depart: '', arrive: '', notes: '' };

const HARDCODED_DESTINATIONS = ['thailand', 'indonesia', 'vietnam', 'singapore', 'malaysia', 'bhutan', 'maldives', 'uae', 'sikkim', 'kashmir', 'ladakh', 'himachal', 'goa', 'kerala', 'rajasthan', 'andaman'];
const HARDCODED_THEMES = ['honeymoon', 'adventure', 'wildlife', 'beach', 'family', 'pilgrimage', 'cruise'];
const FB_TYPES = ['default', 'beach', 'mountain', 'city', 'family', 'couple', 'temple', 'wildlife', 'adventure'];

const Toggle = ({ checked, onChange, label, testid }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid={testid}>
    <span className="text-xs text-gray-600">{label}</span>
    <span
      onClick={() => onChange(!checked)}
      className={`relative inline-block w-10 h-5 rounded-full transition-colors ${checked ? 'bg-[#0d64a8]' : 'bg-gray-300'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </span>
  </label>
);

const PackageManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | pkg_code
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'published' | 'draft'
  const [q, setQ] = useState('');
  const [destinations, setDestinations] = useState(HARDCODED_DESTINATIONS);
  const [themes, setThemes] = useState(HARDCODED_THEMES);

  useEffect(() => { load(); loadTaxonomies(); }, []);

  const loadTaxonomies = async () => {
    try {
      const res = await axios.get(`${API}/taxonomies`);
      const list = res.data || [];
      const d = list.filter((t) => t.type === 'destination').map((t) => t.slug).filter(Boolean);
      const th = list.filter((t) => t.type === 'theme').map((t) => t.slug).filter(Boolean);
      const mergedD = Array.from(new Set([...HARDCODED_DESTINATIONS, ...d]));
      const mergedT = Array.from(new Set([...HARDCODED_THEMES, ...th]));
      setDestinations(mergedD);
      setThemes(mergedT);
    } catch (_) {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/packages`, { headers: adminAuthHeader() });
      setItems(res.data || []);
    } catch (_) { setItems([]); } finally { setLoading(false); }
  };

  const startNew = () => { setEditing('new'); setForm(empty); setErr(''); };
  const startEdit = (p) => {
    setEditing(p.pkg_code);
    setForm({
      ...p,
      themes: (p.themes || []).join(', '),
      highlights: (p.highlights || []).join('\n'),
      inclusions: (p.inclusions || []).join('\n'),
      exclusions: (p.exclusions || []).join('\n'),
      hotels: Array.isArray(p.hotels) ? p.hotels.map((h) => ({ ...emptyHotel, ...h })) : [],
      flights: Array.isArray(p.flights) ? p.flights : [],
      show_hotels: p.show_hotels !== false,
      show_flights: p.show_flights !== false,
      additional_info: p.additional_info || '',
      offer_enabled: !!p.offer_enabled,
      offer_price: p.offer_price || 0,
      offer_ribbon_text: p.offer_ribbon_text || 'Limited Offer',
      offer_ends_at: p.offer_ends_at ? String(p.offer_ends_at).slice(0, 16) : '', // YYYY-MM-DDTHH:mm for datetime-local
      seo_title: p.seo_title || '',
      seo_description: p.seo_description || '',
      seo_og_image: p.seo_og_image || '',
    });
    setErr('');
  };
  const cancel = () => { setEditing(null); setForm(empty); setErr(''); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Hotel/flight list helpers
  const addHotel = () => setForm((f) => ({ ...f, hotels: [...(f.hotels || []), { ...emptyHotel }] }));
  const updHotel = (i, k, v) => setForm((f) => ({
    ...f,
    hotels: (f.hotels || []).map((h, idx) => (idx === i ? { ...h, [k]: v } : h)),
  }));
  const rmHotel = (i) => setForm((f) => ({ ...f, hotels: (f.hotels || []).filter((_, idx) => idx !== i) }));

  const addFlight = () => setForm((f) => ({ ...f, flights: [...(f.flights || []), { ...emptyFlight }] }));
  const updFlight = (i, k, v) => setForm((f) => ({
    ...f,
    flights: (f.flights || []).map((h, idx) => (idx === i ? { ...h, [k]: v } : h)),
  }));
  const rmFlight = (i) => setForm((f) => ({ ...f, flights: (f.flights || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setErr('');
    if (!form.pkg_code.trim()) return setErr('Package Code is required');
    if (!form.title.trim()) return setErr('Title is required');
    const arr = (s) => (typeof s === 'string' ? s.split(/[,\n]/).map((x) => x.trim()).filter(Boolean) : (s || []));
    const lines = (s) => (typeof s === 'string' ? s.split('\n').map((x) => x.trim()).filter(Boolean) : (s || []));
    const payload = {
      ...form,
      pkg_code: form.pkg_code.trim(),
      price: Number(form.price) || 0,
      themes: arr(form.themes),
      highlights: lines(form.highlights),
      inclusions: lines(form.inclusions),
      exclusions: lines(form.exclusions),
      hotels: (form.hotels || []).filter((h) => (h.name || '').trim()),
      flights: (form.flights || []).filter((f) => (f.airline || f.from_city || f.to_city)),
      show_hotels: !!form.show_hotels,
      show_flights: !!form.show_flights,
      additional_info: (form.additional_info || '').trim(),
      offer_enabled: !!form.offer_enabled,
      offer_price: Number(form.offer_price) || 0,
      offer_ribbon_text: (form.offer_ribbon_text || '').trim() || 'Limited Offer',
      offer_ends_at: form.offer_ends_at
        ? new Date(form.offer_ends_at).toISOString()
        : null,
    };
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/packages`, payload, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/packages/${editing}`, payload, { headers: adminAuthHeader() });
      }
      await load();
      cancel();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete package ${code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/packages/${code}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) {}
  };

  const [downloading, setDownloading] = useState('');
  const [copiedCode, setCopiedCode] = useState('');

  const copyLiveLink = async (code) => {
    const url = `${window.location.origin}/package/${code}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? '' : c)), 1800);
    } catch (_) {
      window.prompt('Copy this link:', url);
    }
  };
  const downloadPdf = async (code, title) => {
    setDownloading(code);
    try {
      const res = await axios.get(`${API}/admin/packages/${encodeURIComponent(code)}/pdf`, {
        headers: adminAuthHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (title || code || 'package').replace(/[^a-zA-Z0-9-_]/g, '-').slice(0, 60);
      a.download = `Travelsera-${code}-${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      alert('Could not generate PDF. Please try again.');
    } finally { setDownloading(''); }
  };

  const togglePublished = async (p) => {
    const next = !(p.published !== false);
    // Optimistic UI
    setItems((list) => list.map((it) => (it.pkg_code === p.pkg_code ? { ...it, published: next } : it)));
    try {
      const payload = { ...p, published: next };
      // Ensure lists are arrays (backend expects List)
      payload.themes = payload.themes || [];
      payload.highlights = payload.highlights || [];
      payload.inclusions = payload.inclusions || [];
      payload.exclusions = payload.exclusions || [];
      payload.hotels = payload.hotels || [];
      payload.flights = payload.flights || [];
      await axios.put(`${API}/admin/packages/${p.pkg_code}`, payload, { headers: adminAuthHeader() });
    } catch (_) {
      // revert
      setItems((list) => list.map((it) => (it.pkg_code === p.pkg_code ? { ...it, published: p.published } : it)));
    }
  };

  const filtered = (filter === 'all' ? items : items.filter((p) => p.category === filter))
    .filter((p) => {
      if (statusFilter === 'published') return p.published !== false;
      if (statusFilter === 'draft') return p.published === false;
      return true;
    })
    .filter((p) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      const hay = [p.title, p.pkg_code, p.destination, p.category, p.short_description, (p.themes || []).join(' ')]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });

  if (editing !== null) {
    const isNew = editing === 'new';
    const liveUrl = !isNew ? `${window.location.origin}/package/${form.pkg_code || editing}` : '';
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">
            {isNew ? 'New Package' : `Editing: ${form.title || form.pkg_code}`}
          </h3>
          <div className="flex gap-2">
            <button onClick={cancel} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-200 rounded-md">
              <X size={14} className="inline mr-1" /> Cancel
            </button>
            <button onClick={save} disabled={saving} className="text-sm bg-[#0d64a8] hover:bg-[#0a548e] text-white px-4 py-1.5 rounded-md disabled:opacity-60">
              <Save size={14} className="inline mr-1" /> {saving ? 'Saving...' : 'Save Package'}
            </button>
          </div>
        </div>

        {!isNew && (
          <div className="mb-4 bg-[#f0f7ff] border border-[#cfe4f7] rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="editor-live-link">
            <Link2 size={16} className="text-[#0d64a8] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-[#0d64a8] font-semibold">Live link (share with customers)</div>
              <div className="text-xs text-gray-700 truncate font-mono">{liveUrl}</div>
            </div>
            <button
              type="button"
              onClick={() => copyLiveLink(form.pkg_code || editing)}
              data-testid="editor-copy-link"
              className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 transition ${copiedCode === (form.pkg_code || editing) ? 'border-green-300 text-green-700 bg-green-50' : 'border-[#0d64a8] text-[#0d64a8] hover:bg-white'}`}
            >
              {copiedCode === (form.pkg_code || editing) ? <><Check size={13}/> Copied!</> : <><Link2 size={13}/> Copy link</>}
            </button>
            <a
              href={`/package/${form.pkg_code || editing}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:border-[#0d64a8] hover:text-[#0d64a8] flex items-center gap-1.5"
              data-testid="editor-open-link"
            >
              <Eye size={13}/> Preview
            </a>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Package Code *</label>
              <input value={form.pkg_code} onChange={(e) => upd('pkg_code', e.target.value)} disabled={editing !== 'new'} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] disabled:bg-gray-50" placeholder="e.g. 118575" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category} onChange={(e) => upd('category', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]">
                <option value="india">India</option>
                <option value="international">International</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input value={form.title} onChange={(e) => upd('title', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]" placeholder="Bali - Nusa Penida" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
              <input value={form.duration} onChange={(e) => upd('duration', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="5 Nights, 6 Days" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Price (INR)</label>
              <input type="number" step="1" min="0" value={form.price} onChange={(e) => upd('price', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="29022" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Itinerary Summary</label>
            <input value={form.itinerary} onChange={(e) => upd('itinerary', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="3N Kuta, 1N Nusa Penida, 2N Ubud" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Destination Slug</label>
              <select value={form.destination || ''} onChange={(e) => upd('destination', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]" data-testid="pkg-destination-select">
                <option value="">— None —</option>
                {destinations.filter(Boolean).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Themes (comma sep.)</label>
              <input value={form.themes} onChange={(e) => upd('themes', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder={themes.join(', ')} data-testid="pkg-themes-input" />
            </div>
          </div>
          <div className="md:col-span-2">
            <ImageUploader value={form.image} onChange={(url) => upd('image', url)} label="Package Image" hint="Landscape works best, 800×500 or larger" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fallback Theme</label>
              <select value={form.fbType} onChange={(e) => upd('fbType', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]">
                {FB_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.published ? 'published' : 'draft'} onChange={(e) => upd('published', e.target.value === 'published')} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]">
                <option value="published">Published</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Overview</label>
            <textarea value={form.overview} onChange={(e) => upd('overview', e.target.value)} rows={3} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" placeholder="Rich description shown on the package detail page" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Highlights (one per line)</label>
            <textarea value={form.highlights} onChange={(e) => upd('highlights', e.target.value)} rows={4} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" placeholder={'Handpicked hotels\nPrivate AC vehicle\nEnglish speaking guide'} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Inclusions (one per line)</label>
            <textarea value={form.inclusions} onChange={(e) => upd('inclusions', e.target.value)} rows={4} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Exclusions (one per line)</label>
            <textarea value={form.exclusions} onChange={(e) => upd('exclusions', e.target.value)} rows={3} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" />
          </div>
        </div>

        {/* Offer / Sale Price */}
        <div className="mt-6 border-t border-gray-100 pt-5" data-testid="offer-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-[11px] font-bold">%</span>
              <h4 className="text-sm font-semibold text-gray-800">Offer &amp; Countdown</h4>
              <span className="text-[10px] text-gray-400 uppercase tracking-widest">Show a sale price and a &ldquo;Limited Offer&rdquo; ribbon on this package</span>
            </div>
            <Toggle
              testid="toggle-offer-enabled"
              checked={!!form.offer_enabled}
              onChange={(v) => upd('offer_enabled', v)}
              label={form.offer_enabled ? 'Offer is LIVE' : 'Offer is off'}
            />
          </div>

          {form.offer_enabled && (
            <div className="bg-rose-50/40 border border-rose-100 rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Offer Price (INR) *</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={form.offer_price}
                  onChange={(e) => upd('offer_price', e.target.value)}
                  placeholder="e.g. 22999"
                  data-testid="offer-price-input"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] bg-white"
                />
                <p className="text-[10px] text-gray-500 mt-1">Must be lower than the original price ({Math.round(Number(form.price || 0)).toLocaleString('en-IN')}).</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Ribbon Text</label>
                <input
                  value={form.offer_ribbon_text}
                  onChange={(e) => upd('offer_ribbon_text', e.target.value)}
                  placeholder="Limited Offer"
                  maxLength={22}
                  data-testid="offer-ribbon-text"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] bg-white"
                />
                <p className="text-[10px] text-gray-500 mt-1">Shown on the diagonal corner ribbon (max ~20 chars).</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Ends At <span className="text-gray-400">(optional)</span></label>
                <input
                  type="datetime-local"
                  value={form.offer_ends_at || ''}
                  onChange={(e) => upd('offer_ends_at', e.target.value)}
                  data-testid="offer-ends-input"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] bg-white"
                />
                <p className="text-[10px] text-gray-500 mt-1">Set a deadline to display a live countdown on the card.</p>
              </div>
              {Number(form.offer_price) > 0 && Number(form.offer_price) < Number(form.price || 0) && (
                <div className="md:col-span-3 flex items-center gap-3 bg-white border border-emerald-200 rounded-md px-3 py-2">
                  <span className="text-xs text-gray-500 uppercase tracking-widest">Preview</span>
                  <span className="text-xs text-gray-400 line-through">INR {Math.round(Number(form.price || 0)).toLocaleString('en-IN')}</span>
                  <span className="text-emerald-600 font-bold">INR {Math.round(Number(form.offer_price)).toLocaleString('en-IN')}</span>
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                    {Math.round(((Number(form.price || 0) - Number(form.offer_price)) / Number(form.price || 1)) * 100)}% OFF
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hotels */}
        <div className="mt-6 border-t border-gray-100 pt-5" data-testid="hotels-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Building2 size={16} className="text-[#0d64a8]" />
              <h4 className="text-sm font-semibold text-gray-800">Hotel Details</h4>
              <span className="text-[10px] text-gray-400 uppercase tracking-widest">Property-wise stay plan</span>
            </div>
            <div className="flex items-center gap-3">
              <Toggle
                testid="toggle-show-hotels"
                checked={!!form.show_hotels}
                onChange={(v) => upd('show_hotels', v)}
                label={form.show_hotels ? 'Visible on website' : 'Hidden from website'}
              />
              <button type="button" onClick={addHotel} data-testid="add-hotel-btn" className="text-xs bg-[#0d64a8] text-white px-3 py-1.5 rounded-md flex items-center gap-1">
                <Plus size={12}/> Add Hotel
              </button>
            </div>
          </div>
          {(form.hotels || []).length === 0 && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-md p-4 text-center">
              No hotels added yet. Click <b>Add Hotel</b> to list the properties in this itinerary.
            </div>
          )}
          <div className="space-y-3">
            {(form.hotels || []).map((h, i) => (
              <div key={i} data-testid={`hotel-row-${i}`} className="bg-gray-50 border border-gray-100 rounded-md p-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                  <input value={h.name} onChange={(e) => updHotel(i, 'name', e.target.value)} placeholder="Hotel name" className="md:col-span-2 border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                  <input value={h.city} onChange={(e) => updHotel(i, 'city', e.target.value)} placeholder="City" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                  <input value={h.nights} onChange={(e) => updHotel(i, 'nights', e.target.value)} placeholder="Nights (e.g. 2)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                  <input value={h.category} onChange={(e) => updHotel(i, 'category', e.target.value)} placeholder="Category (5 Star)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                  <button type="button" onClick={() => rmHotel(i)} data-testid={`hotel-remove-${i}`} className="text-xs text-red-500 border border-gray-200 rounded px-2 py-1.5 hover:bg-red-50 flex items-center justify-center gap-1"><Trash2 size={12}/> Remove</button>
                </div>
                <input value={h.notes} onChange={(e) => updHotel(i, 'notes', e.target.value)} placeholder="Notes / room type (optional)" className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <ImageUploader
                    value={h.image}
                    onChange={(url) => updHotel(i, 'image', url)}
                    label={`Hotel Photo 1`}
                    hint="Landscape works best · used on the website & PDF"
                    height="h-28"
                  />
                  <ImageUploader
                    value={h.image2}
                    onChange={(url) => updHotel(i, 'image2', url)}
                    label={`Hotel Photo 2`}
                    hint="Second image shown on the site as a gallery"
                    height="h-28"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Flights */}
        <div className="mt-6 border-t border-gray-100 pt-5" data-testid="flights-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Plane size={16} className="text-[#0d64a8]" />
              <h4 className="text-sm font-semibold text-gray-800">Flight Details</h4>
              <span className="text-[10px] text-gray-400 uppercase tracking-widest">Optional, shown to travellers</span>
            </div>
            <div className="flex items-center gap-3">
              <Toggle
                testid="toggle-show-flights"
                checked={!!form.show_flights}
                onChange={(v) => upd('show_flights', v)}
                label={form.show_flights ? 'Visible on website' : 'Hidden from website'}
              />
              <button type="button" onClick={addFlight} data-testid="add-flight-btn" className="text-xs bg-[#0d64a8] text-white px-3 py-1.5 rounded-md flex items-center gap-1">
                <Plus size={12}/> Add Flight
              </button>
            </div>
          </div>
          {(form.flights || []).length === 0 && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-md p-4 text-center">
              No flights added yet. Click <b>Add Flight</b> to include airline segments.
            </div>
          )}
          <div className="space-y-3">
            {(form.flights || []).map((fl, i) => (
              <div key={i} data-testid={`flight-row-${i}`} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-start bg-gray-50 border border-gray-100 rounded-md p-3">
                <input value={fl.airline} onChange={(e) => updFlight(i, 'airline', e.target.value)} placeholder="Airline (IndiGo)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <input value={fl.flight_no} onChange={(e) => updFlight(i, 'flight_no', e.target.value)} placeholder="Flight # (6E 2035)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <input value={fl.from_city} onChange={(e) => updFlight(i, 'from_city', e.target.value)} placeholder="From (DEL)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <input value={fl.to_city} onChange={(e) => updFlight(i, 'to_city', e.target.value)} placeholder="To (BKK)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <input value={fl.depart} onChange={(e) => updFlight(i, 'depart', e.target.value)} placeholder="Depart (10 Jun 06:30)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <input value={fl.arrive} onChange={(e) => updFlight(i, 'arrive', e.target.value)} placeholder="Arrive (11:45)" className="border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <input value={fl.notes} onChange={(e) => updFlight(i, 'notes', e.target.value)} placeholder="Notes (baggage, class) — optional" className="md:col-span-5 border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:border-[#0d64a8]" />
                <button type="button" onClick={() => rmFlight(i)} data-testid={`flight-remove-${i}`} className="text-xs text-red-500 border border-gray-200 rounded px-2 py-1.5 hover:bg-red-50 flex items-center justify-center gap-1"><Trash2 size={12}/> Remove</button>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 border-t border-gray-100 pt-5" data-testid="additional-info-section">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-[#0d64a8]" />
            <h4 className="text-sm font-semibold text-gray-800">Additional Information</h4>
            <span className="text-[10px] text-gray-400 uppercase tracking-widest">Free-form section for special notes</span>
          </div>
          <textarea
            value={form.additional_info || ''}
            onChange={(e) => upd('additional_info', e.target.value)}
            rows={5}
            placeholder="Any extra information you'd like to show below Hotels & Flights on the website — visa tips, dress code, weather advice, offer notes, terms specific to this trip, etc. Line breaks preserved."
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"
            data-testid="additional-info-input"
          />
        </div>

        {/* SEO */}
        <div className="mt-6 border-t border-gray-100 pt-5">
          <SEOSettingsBlock
            values={{ seo_title: form.seo_title, seo_description: form.seo_description, seo_og_image: form.seo_og_image }}
            onChange={upd}
            heading="SEO Settings"
            subheading="Overrides the global SEO defaults for this package page. Leave empty to fall back."
            testIdPrefix="package-seo"
          />
        </div>

        {err && <div className="mt-4 bg-red-50 border border-red-100 text-red-700 text-sm px-3 py-2 rounded-md">{err}</div>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Packages</h2>
          <p className="text-sm text-gray-500">Add, edit and organise your holiday packages.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]" data-testid="pkg-category-filter">
            <option value="all">All ({items.length})</option>
            <option value="india">India ({items.filter((p) => p.category === 'india').length})</option>
            <option value="international">International ({items.filter((p) => p.category === 'international').length})</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]" data-testid="pkg-status-filter">
            <option value="all">All statuses</option>
            <option value="published">Published only</option>
            <option value="draft">Drafts only</option>
          </select>
          <button onClick={startNew} data-testid="pkg-new-btn" className="flex items-center gap-1.5 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-4 py-2 rounded-md">
            <Plus size={15} /> New Package
          </button>
        </div>
      </div>

      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search title, package code, destination, theme..." testId="pkg-search" />
      </div>


      {loading ? (
        <div className="text-center py-16 text-gray-500 text-sm">Loading packages...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <p className="text-gray-700 font-medium">No packages yet</p>
          <button onClick={startNew} className="mt-4 bg-[#0d64a8] text-white text-sm px-5 py-2 rounded-md">Create your first package</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((p) => (
            <div key={p.pkg_code} className="bg-white border border-gray-200 rounded-lg p-3 flex items-start gap-3 hover:shadow-sm transition">
              {p.image && (
                <img src={resolveImg(p.image)} alt={p.title} className="w-24 h-20 rounded-md object-cover shrink-0 border border-gray-100" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] bg-[#e8f2fb] text-[#0d64a8] px-1.5 py-0.5 rounded font-semibold">#{p.pkg_code}</span>
                  <h3 className="font-semibold text-gray-900 text-sm truncate">{p.title}</h3>
                  {p.published === false ? (
                    <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded flex items-center gap-0.5"><EyeOff size={9}/> DRAFT</span>
                  ) : (
                    <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Eye size={9}/> LIVE</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1">
                  <span className="flex items-center gap-1"><Clock size={10}/> {p.duration}</span>
                  {p.destination && <span className="flex items-center gap-1"><MapPin size={10}/> {p.destination}</span>}
                  <span className="text-[#0d64a8] font-semibold">INR {Math.round((p.price || 0)).toLocaleString('en-IN')}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1 line-clamp-1">{p.itinerary}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  onClick={() => copyLiveLink(p.pkg_code)}
                  data-testid={`pkg-copy-link-${p.pkg_code}`}
                  title={copiedCode === p.pkg_code ? 'Copied!' : 'Copy live link (share with customers)'}
                  className={`w-8 h-8 rounded-md border flex items-center justify-center transition ${
                    copiedCode === p.pkg_code
                      ? 'border-green-300 text-green-600 bg-green-50'
                      : 'border-gray-200 text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]'
                  }`}
                >
                  {copiedCode === p.pkg_code ? <Check size={13} /> : <Link2 size={13} />}
                </button>
                <button
                  onClick={() => togglePublished(p)}
                  data-testid={`pkg-toggle-${p.pkg_code}`}
                  title={p.published !== false ? 'Hide from website (make Draft)' : 'Show on website (Publish)'}
                  className={`w-8 h-8 rounded-md border flex items-center justify-center transition ${
                    p.published !== false
                      ? 'border-green-200 text-green-600 hover:bg-green-50'
                      : 'border-gray-200 text-gray-400 hover:border-[#0d64a8] hover:text-[#0d64a8]'
                  }`}
                >
                  {p.published !== false ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
                <button
                  onClick={() => downloadPdf(p.pkg_code, p.title)}
                  disabled={downloading === p.pkg_code}
                  data-testid={`pkg-pdf-${p.pkg_code}`}
                  title="Download seller PDF brochure"
                  className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8] disabled:opacity-60"
                >
                  {downloading === p.pkg_code ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
                </button>
                <button onClick={() => startEdit(p)} data-testid={`pkg-edit-${p.pkg_code}`} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]">
                  <Edit3 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PackageManager;
